import '~/styles/components/page-card/page-card.scss';

import { Icon } from '@design-system/icon';
import classNames from 'classnames';
import { withPrefix } from 'gatsby';
import React, { FC } from 'react';

import { useSiteSettings } from '~/context/SiteSettingsContext';
import { useTranslations } from '~/context/TranslationContext/TranslationsContext';
import { imageMapper } from '~/mappers/image';
import { getCardsCategoriesLabels } from '~/utils/getCardsCategoriesLabels';

import { Card } from '../card';
import { PageCardProps } from './models';

const BASE_CLASS = 'cds-page-card';

export const PageCard: FC<PageCardProps> = ({
  className,
  link,
  variant,
  cardContent,
  cardCta,
  cardMedia,
  cardLabel,
  categories,
  fullCategories,
  meta,
}) => {
  const t = useTranslations();
  const siteSettings = useSiteSettings();

  const fallbackMedia = siteSettings?.fallbackImage
    ? imageMapper(siteSettings.fallbackImage)
    : undefined;
  const effectiveCardMedia =
    cardMedia ?? (fallbackMedia ? { image: fallbackMedia } : undefined);

  const categoriesLabels = getCardsCategoriesLabels(fullCategories);

  const { headingBlock, disclaimer, category } = cardContent;
  const headingBlockWithLink = { ...headingBlock, headlineLink: link };

  const categoryClasses =
    categories?.map((title) => `${BASE_CLASS}--${title}`) || [];

  return (
    <Card
      className={classNames(BASE_CLASS, ...categoryClasses, className)}
      hasMedia={!!effectiveCardMedia}
      variant={variant}
    >
      <Card.CardContent
        headingBlock={headingBlockWithLink}
        disclaimer={disclaimer}
        category={category}
        meta={meta}
      >
        {cardCta && <Card.CardCTA {...cardCta} />}

        {(link || meta?.resourceLink) && (
          <div className="card-resource-group">
            {link && (
              <span>
                <a className="card__cta" href={link?.props?.href}>
                  <span className="card__ctaIcon" aria-hidden="true">
                    <Icon size={15} icon="arrowRight" />
                  </span>
                  <span className="card__ctaLabel">
                    {t?.card?.learnMoreButton}
                  </span>
                </a>
              </span>
            )}
            {meta?.resourceLink && (
              <span className="card-resource--download">
                <a className="card__cta" href={meta?.resourceLink}>
                  <span className="card__icon" aria-hidden="true">
                    <img
                      src={withPrefix(`/images/download.svg`)}
                      alt="download.svg"
                      className="card__cta--download-resources"
                    />
                  </span>
                  <span className="card__ctaLabel">
                    {t?.card?.downloadButton}
                  </span>
                </a>
              </span>
            )}
          </div>
        )}
      </Card.CardContent>
      {effectiveCardMedia || cardLabel ? (
        <Card.CardMedia
          {...(variant === 'standard' ? { mediaVariant: 'rounded' } : {})}
          {...effectiveCardMedia}
        >
          <div className="cds-card__label-group">
            {cardLabel && <Card.CardLabel {...cardLabel} />}
            {categoriesLabels.length > 0 &&
              categoriesLabels
                .slice(0, 6)
                .map((cardLabelProps) => (
                  <Card.CardLabel
                    key={cardLabelProps.labelContent}
                    {...cardLabelProps}
                  />
                ))}
          </div>
        </Card.CardMedia>
      ) : null}
    </Card>
  );
};
